import {
  cn,
  Icon,
  TokenIcon,
  type TokenIconSymbol,
  Typography,
} from "@kyan/ui";

import {
  formatInstrumentName,
  getOptionInstrumentProperties,
  getPerpInstrumentProperties,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { toSide } from "#/utils/marketActionConverter";
import { formatCurrency } from "#/utils/valueFormatters";

export interface InstrumentTagProps {
  price: number;
  isBuy?: boolean;
  disabled?: boolean;
  isActive?: boolean;
  className?: string;
  instrumentName: string;
  onClick?: () => void;
  onRemove?: () => void;
}

export const InstrumentTag: React.FC<InstrumentTagProps> = ({
  price,
  isBuy = true,
  disabled,
  isActive,
  className,
  instrumentName,
  onClick,
  onRemove,
}) => {
  const isOption = isOptionInstrument(instrumentName);
  const { underlying } = isOption
    ? getOptionInstrumentProperties(instrumentName)
    : getPerpInstrumentProperties(instrumentName);

  const formattedPrice = formatCurrency(price);

  const isClickable = Boolean(onClick) && !disabled;
  const priceText = isOption
    ? `Best ${toSide(isBuy)} ${formattedPrice}`
    : `Market price ${formattedPrice}`;

  return (
    <div
      className={cn("relative", className, {
        "-m-2": isClickable,
      })}
    >
      <button
        className={cn(
          "flex w-full items-center justify-between border border-transparent transition-colors",
          {
            "cursor-pointer rounded p-2": isClickable,
            "hover:bg-buttonHover": isClickable && !isActive,
            "dark:bg-buttonHover border-primary border shadow-[0px_2px_14px_0px_rgba(26,26,26,0.14)]":
              isClickable && isActive,
            "opacity-30": disabled,
          },
        )}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <div className="bg-textSecondary/10 flex rounded">
            <TokenIcon
              size="sm"
              className="text-textPrimary h-7 w-10"
              symbol={underlying.toLowerCase() as TokenIconSymbol}
            />
          </div>

          <div className="grid place-items-start">
            <Typography primary>
              {formatInstrumentName(instrumentName)}
            </Typography>
            <Typography size={11} className="capitalize">
              {priceText}
            </Typography>
          </div>
        </div>

        {onRemove && (
          <Icon
            size="sm"
            name="cross"
            disabled={disabled}
            hoverEffect="circle"
            className={cn({
              "hover:text-textPrimary transition-colors":
                isClickable && !isActive,
              "dark:hover:text-textPrimary": isClickable && isActive,
            })}
            onClick={onRemove}
          />
        )}
      </button>
    </div>
  );
};
